<template>
  <div class="container mt-4 mb-5" style="max-width: 760px">
    <LoadingSpinner :loading="loading"></LoadingSpinner>
    <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>

    <h3>Kundeklarskjerm (ODS)</h3>
    <p>Bestillingsnumrene for gjestene på en skjerm i restauranten: «Under tilberedning» og «Klar til henting». Bruk en
      TV eller skjerm dere allerede har — alt som trengs er en nettleser. Så mange skjermer dere vil.</p>

    <div v-if="locationsWithoutNumbers.length" class="alert alert-warning">
      <strong>{{ locationsWithoutNumbers.map(l => l.name).join(', ') }}</strong> bruker ikke bestillingsnummer. Gjestene har
      da ikke et kort nummer å se etter, og skjermen viser lange ordrenumre. Slå på «Bestillingsnummer for gjestene» under
      <a href="/locations">Avdelinger</a>.
    </div>

    <div class="ods-section mt-4">
      <h5>Koble til en skjerm</h5>
      <ol class="ods-steps">
        <li>Åpne <strong>{{ odsUrl }}</strong> i nettleseren på skjermen. Den viser en kode på seks tegn.</li>
        <li>Skriv inn koden her og velg avdeling.</li>
      </ol>
      <div class="row g-2">
        <div class="col-sm-4">
          <span class="ingredient-title">Kode</span>
          <input class="form-control ods-code" type="text" maxlength="7" v-model="pairCode" placeholder="ABC234" autocomplete="off" />
        </div>
        <div class="col-sm-4">
          <span class="ingredient-title">Navn på skjermen</span>
          <input class="form-control" type="text" maxlength="60" v-model="pairName" placeholder="Disk" />
        </div>
        <div v-if="locations.length > 1" class="col-sm-4">
          <span class="ingredient-title">Avdeling</span>
          <select class="form-select" v-model="pairLocationId">
            <option v-for="location in locations" :key="location.id" :value="location.id">{{ location.name }}</option>
          </select>
        </div>
      </div>
      <button type="button" class="bo_btn mt-3" :disabled="!pairCode.trim() || !pairLocationId || busy" @click="pair">Koble til</button>
      <div v-if="pairMessage" class="mt-2" :class="pairFailed ? 'text-danger' : 'text-success'">{{ pairMessage }}</div>
    </div>

    <div class="ods-section mt-4">
      <h5>Skjerm som bare tar en adresse</h5>
      <p class="bo_form-hint mt-0">For digital signage (Samsung MagicInfo, Yodeck, ScreenCloud …) der ingen kan trykke på
        skjermen: lag en lenke og lim den inn i spilleren.</p>
      <div class="row g-2">
        <div class="col-sm-6">
          <span class="ingredient-title">Navn på skjermen</span>
          <input class="form-control" type="text" maxlength="60" v-model="linkName" placeholder="TV ved inngangen" />
        </div>
        <div v-if="locations.length > 1" class="col-sm-6">
          <span class="ingredient-title">Avdeling</span>
          <select class="form-select" v-model="linkLocationId">
            <option v-for="location in locations" :key="location.id" :value="location.id">{{ location.name }}</option>
          </select>
        </div>
      </div>
      <button type="button" class="bo_btn_secondary mt-3" :disabled="!linkLocationId || busy" @click="createLink">Lag skjermlenke</button>
      <div v-if="createdLink" class="ods-link mt-3">
        <div class="d-flex gap-2">
          <input class="form-control" type="text" readonly :value="createdLink" @focus="$event.target.select()" />
          <button type="button" class="bo_btn" @click="copyLink">{{ copied ? 'Kopiert' : 'Kopier' }}</button>
        </div>
        <small class="text-muted d-block mt-1">Lenken vises bare nå. Den viser bestillingsnumrene for denne avdelingen — gi den bare til
          den som setter opp skjermen. Trenger dere en ny, fjern skjermen og lag en ny lenke.</small>
      </div>
      <div v-if="linkMessage" class="mt-2 text-danger">{{ linkMessage }}</div>
    </div>

    <div class="ods-section mt-4">
      <h5>Skjermer</h5>
      <p v-if="displays.length === 0" class="text-muted">Ingen skjermer koblet til ennå.</p>
      <div v-for="display in displays" :key="display.id" class="ods-display">
        <button type="button" class="bo_btn_secondary ods-remove" :disabled="busy" @click="remove(display)">
          <i class="fas fa-times"></i> Fjern
        </button>
        <div class="ods-display__info">
          <div v-if="renamingId !== display.id" class="ods-display__name">
            {{ display.name }}
            <a href="#" class="ms-2 small" @click.prevent="startRename(display)">Endre navn</a>
          </div>
          <div v-else class="d-flex gap-2">
            <input class="form-control form-control-sm" type="text" maxlength="60" v-model="renameValue" />
            <button type="button" class="bo_btn" @click="saveRename(display)">Lagre</button>
          </div>
          <div class="text-muted small">
            <span v-if="locations.length > 1">{{ locationName(display.locationId) }} · </span>
            <span :class="isOnline(display) ? 'ods-online' : ''">{{ lastSeenText(display) }}</span>
            <span v-if="display.pairedBy === 'LINK'"> · lenke</span>
          </div>
        </div>
        <button type="button" class="bo_btn_secondary" :disabled="busy" @click="reload(display)">Last på nytt</button>
      </div>
      <p class="bo_form-hint mt-3">«Sist sett» oppdateres omtrent hvert femte minutt mens skjermen er på.
        «Last på nytt» laster skjermen på nytt neste gang den henter tall — nyttig når noe ser rart ut.</p>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue.vue";
import OdsService from "@/services/ods/OdsService";
import LocationService from "@/services/location/LocationService";

/**
 * Kundeklarskjerm (ODS): the guests' order displays (TastyODS). A screen opens the TastyODS address and shows a code; typed here, the screen is
 * connected to a location. Signage players that only take an address get a link with the screen's key in it instead.
 * The screens poll TastyODS, never CagistaServices; this page only manages them. Architecture: TastyODS docs/architecture.md.
 */
// VERIFY the production address once the TastyODS EC2 node and its DNS exist.
const ODS_URL = import.meta.env.VITE_ODS_URL || (import.meta.env.PROD ? 'https://ods.cagista.com' : 'http://127.0.0.1:3040');
const ONLINE_MINUTES = 10;
const MESSAGES = {
  INVALID_CODE: 'Koden har seks tegn — bokstaver og tall, uten 0, O, 1, I og L.',
  CODE_NOT_FOUND: 'Fant ikke koden. Sjekk at den står på skjermen nå — den byttes etter 15 minutter.',
  CODE_USED: 'Denne koden er allerede brukt. Skjermen viser en ny kode om litt.',
  NOT_YOUR_LOCATION: 'Velg en av deres avdelinger.'
};

export default {
  components: {LoadingSpinner, ConfirmDialogue},
  data() {
    return {
      loading: true,
      busy: false,
      odsUrl: ODS_URL.replace(/^https?:\/\//, ''),
      locations: [],
      displays: [],
      pairCode: '',
      pairName: '',
      pairLocationId: '',
      pairMessage: '',
      pairFailed: false,
      linkName: '',
      linkLocationId: '',
      createdLink: '',
      copied: false,
      linkMessage: '',
      renamingId: '',
      renameValue: ''
    };
  },
  computed: {
    locationsWithoutNumbers() {
      return this.locations.filter(location => location.useOrderDisplay !== true);
    }
  },
  mounted() {
    LocationService.listLocations().then(
      (response) => {
        this.locations = (response.data || []).filter(location => location.status !== 'DELETED');
        const first = this.locations.length ? this.locations[0].id : '';
        this.pairLocationId = first;
        this.linkLocationId = first;
        this.loadDisplays();
      },
      (error) => { console.log(error); this.loading = false; }
    );
  },
  methods: {
    loadDisplays() {
      OdsService.getDisplays().then(
        (response) => { this.displays = response.data || []; this.loading = false; },
        (error) => { console.log(error); this.loading = false; }
      );
    },
    locationName(locationId) {
      const location = this.locations.find(l => l.id === locationId);
      return location ? location.name : '';
    },
    isOnline(display) {
      return this.minutesSince(display.lastSeenTime) !== null && this.minutesSince(display.lastSeenTime) <= ONLINE_MINUTES;
    },
    minutesSince(time) {
      if (!time) return null;
      const then = new Date(time).getTime();
      return Number.isNaN(then) ? null : Math.max(0, Math.round((Date.now() - then) / 60000));
    },
    lastSeenText(display) {
      const minutes = this.minutesSince(display.lastSeenTime);
      if (minutes === null) return 'Aldri sett — er adressen åpnet på skjermen?';
      if (minutes < 2) return 'Sist sett nå nettopp';
      if (minutes < 60) return 'Sist sett for ' + minutes + ' min siden';
      if (minutes < 48 * 60) return 'Sist sett for ' + Math.round(minutes / 60) + ' t siden';
      return 'Sist sett ' + new Date(display.lastSeenTime).toLocaleDateString('nb-NO');
    },
    pair() {
      this.busy = true;
      this.pairMessage = '';
      OdsService.pairDisplay(this.pairLocationId, this.pairCode, this.pairName).then(
        (response) => {
          this.busy = false;
          if (response.data && response.data.result === 'SUCCEEDED') {
            this.pairFailed = false;
            this.pairMessage = 'Koblet til. Skjermen viser bestillingsnumrene om noen sekunder.';
            this.pairCode = '';
            this.pairName = '';
            this.loadDisplays();
          } else {
            this.pairFailed = true;
            this.pairMessage = MESSAGES[response.data && response.data.code] || 'Kunne ikke koble til skjermen.';
          }
        },
        (error) => { console.log(error); this.busy = false; this.pairFailed = true; this.pairMessage = 'Kunne ikke koble til skjermen.'; }
      );
    },
    createLink() {
      this.busy = true;
      this.linkMessage = '';
      this.createdLink = '';
      this.copied = false;
      OdsService.createDisplayLink(this.linkLocationId, this.linkName).then(
        (response) => {
          this.busy = false;
          if (response.data && response.data.result === 'SUCCEEDED') {
            this.createdLink = ODS_URL + '/?key=' + encodeURIComponent(response.data.token);
            this.linkName = '';
            this.loadDisplays();
          } else {
            this.linkMessage = MESSAGES[response.data && response.data.code] || 'Kunne ikke lage lenken.';
          }
        },
        (error) => { console.log(error); this.busy = false; this.linkMessage = 'Kunne ikke lage lenken.'; }
      );
    },
    copyLink() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.createdLink).then(() => { this.copied = true; }, () => {});
      }
    },
    reload(display) {
      this.busy = true;
      OdsService.reloadDisplay(display.id).then(
        () => { this.busy = false; },
        (error) => { console.log(error); this.busy = false; }
      );
    },
    startRename(display) {
      this.renamingId = display.id;
      this.renameValue = display.name;
    },
    saveRename(display) {
      OdsService.renameDisplay(display.id, this.renameValue).then(
        () => { this.renamingId = ''; this.loadDisplays(); },
        (error) => { console.log(error); }
      );
    },
    async remove(display) {
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Fjern skjerm',
        message: 'Fjerne «' + display.name + '»? Skjermen slutter å vise bestillingsnumrene innen et minutt og viser en ny kode.'
          + (display.pairedBy === 'LINK' ? ' Lenken virker ikke lenger.' : ''),
        okButton: 'Fjern', cancelButton: 'Avbryt'
      });
      if (!ok) return;
      this.busy = true;
      OdsService.removeDisplay(display.id).then(
        () => { this.busy = false; this.loadDisplays(); },
        (error) => { console.log(error); this.busy = false; }
      );
    }
  }
};
</script>

<style scoped>
.ods-section {
  border-top: 1px solid var(--bo-muted-2, #ddd);
  padding-top: 16px;
}
.ods-steps {
  padding-left: 20px;
}
.ods-code {
  text-transform: uppercase;
  letter-spacing: 3px;
  font-family: monospace;
}
.ods-display {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--bo-muted-2, #eee);
}
.ods-display__info {
  flex: 1;
}
.ods-display__name {
  font-weight: 600;
}
.ods-online {
  color: #2E7D32;
}
</style>
